import "./HomeDetails.css";
import { useState, useRef, useEffect } from "react";
import fotoPortada from "../images/fotoPortada.jpeg";
import fotoRoadmap from "../images/Roadmap_TheQAHub_2026.jpg";
import webOptimized from "../images/webQA-900.webp";
import mobileOptimized from "../images/mobileQA-900.webp";
import firmwareOptimized from "../images/firmwareQA-900.webp";
import desktopOptimized from "../images/desktopQA-900.webp";
import academyBanner from "../images/theqahub_academy_banner.png";
import { FaLaptopCode, FaMobileAlt, FaMicrochip, FaDesktop } from "react-icons/fa";
import videoSource from "../images/TheQAHub.mp4";
import brandPoster from "../images/TheQAHub-poster.webp";
import BlogDetailsHome from "./home/BlogDetailsHome";
import GradientText from "../blocks/GradientText/GradientText";
import TextPressure from "../blocks/TextPressure/TextPressure";
import ContactInfo from "./ContactInfo";
import { useTranslation } from "../i18n";
import { renderBrandText } from "../utils/brandText";

// Contenido dinámico según el servicio seleccionado
const serviceDetails = {
    web: {
        title: "QA Web",
        description: "Construcción de aplicaciones web modernas con las últimas tecnologías para garantizar rendimiento y escalabilidad.",
        extendedDescription: "Implementación de sitios y plataformas web con una arquitectura robusta, garantizando rendimiento, seguridad y experiencia de usuario optimizada.",
        optimizedImage: webOptimized
    },
    mobile: {
        title: "QA Mobile",
        description: "Automatización y aseguramiento de calidad en apps móviles nativas e híbridas.",
        extendedDescription: "Desde aplicaciones nativas hasta híbridas, optimizo y pruebo cada detalle para garantizar funcionalidad, compatibilidad y experiencia fluida.",
        optimizedImage: mobileOptimized
    },
    firmware: {
        title: " QA Firmware & Embebidos",
        description: "Pruebas de software para hardware embebido y optimización de sistemas de bajo nivel.",
        extendedDescription: "Testing en dispositivos de hardware y software embebido, asegurando la estabilidad y fiabilidad en entornos críticos de desarrollo software.",
        optimizedImage: firmwareOptimized
    },
    desktop: {
        title: "QA Desktop",
        description: "Automatización y testing para software de escritorio en múltiples plataformas.",
        extendedDescription: "Pruebas de software y aseguramiento de calidad en aplicaciones de escritorio, garantizando rendimiento y estabilidad en diferentes sistemas operativos.",
        optimizedImage: desktopOptimized
    }
};

const ProfileImage = ({ loading = "lazy", fetchPriority }) => (
    <img
        src={fotoPortada}
        alt="Diego Sánchez Martín - QA Engineer"
        width="760"
        height="1013"
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
    />
);

function HomeDetails() {
    const { t } = useTranslation();
    const [selectedService, setSelectedService] = useState("web");
    const videoRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile(); // Verifica al cargar

        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);
    
    const handleVideoEnd = () => {
        const video = videoRef.current;
        if (video) {
            video.pause(); // Pausa el video
        }
    };

    return (
        <>
            {/* Sección "Sobre mí" */}
            {isMobile ? (
                <section className="home-details-container">
                    <div className="home-details-image">
                        <ProfileImage />
                    </div>
                    <div className="home-details-text">
                        <div className="section-heading-stack">
                            <span className="section-kicker">{t("home.kickerAbout")}</span>
                            <h2 className="section-title-academy">{t("home.hello")}</h2>
                        </div>
                        <p>
                            {renderBrandText(t("home.intro1"))}
                        </p>
                        <p>
                            {t("home.intro2")}
                        </p>
                        <p>
                            {t("home.intro3Mobile")}
                        </p>
                        <p>
                            {renderBrandText(t("home.intro4"))}
                        </p>
                        <a href="/sobre-mi" className="details-btn">{t("home.aboutCta")}</a>
                    </div>
                </section>
            ) : (
                <section className="home-details-container">
                    <div className="home-details-text">
                        <div className="section-heading-stack">
                            <span className="section-kicker">{t("home.kickerAbout")}</span>
                            <h2 className="section-title-academy">{t("home.hello")}</h2>
                        </div>
                        <p>
                            {renderBrandText(t("home.intro1"))}
                        </p>
                        <p>
                            {t("home.intro2")}
                        </p>
                        <p>
                            {t("home.intro3")}
                        </p>
                        <p>
                            {renderBrandText(t("home.intro4"))}
                        </p>
                        <a href="/sobre-mi" className="details-btn">{t("home.aboutCta")}</a>
                    </div>
                    <div className="home-details-image">
                        <ProfileImage loading="eager" fetchPriority="high" />
                    </div>
                </section>
            )}

            {/* Barra separadora */}
            <hr className="section-divider" />

            <section className="brand-container">
                <div className="brand-content">
                    {/* Contenedor en dos columnas */}
                    <div className="brand-wrapper">
                        {/* Video a la izquierda */}
                        <div className="brand-video">
                            <video 
                                ref={videoRef} 
                                className="video-marca" 
                                autoPlay 
                                muted 
                                playsInline
                                preload="none"
                                poster={brandPoster}
                                onEnded={handleVideoEnd}
                            >
                                <source src={videoSource} type="video/mp4" />
                                {t("common.browserVideo")}
                            </video>
                        </div>

                        {/* Información a la derecha */}
                        <div className="brand-text">
                            <div className="section-heading-stack">
                                <span className="section-kicker">{t("home.kickerBrand")}</span>
                                <h2 className="section-title-academy">{t("home.brandTitle")}</h2>
                            </div>
                            <p>
                                {t("home.brandP1")}
                            </p>
                            <p>
                                {renderBrandText(t("home.brandP2"))}
                            </p>
                            <p>{t("home.interested")}</p>
                            <a href="https://youtu.be/IjYElVyKGJU" target="_blank" rel="noreferrer" className="brand-btn">
                                {renderBrandText(t("home.brandCta"))}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Barra separadora */}
            <hr className="section-divider" />

            {/* Sección Roadmap */}
            <section className="roadmap-centered">

            {/* Texto superior */}
            <div className="roadmap-top section-heading-stack section-heading-stack-center">
                <span className="section-kicker">{t("home.kickerRoadmap")}</span>
                <h2 className="roadmap-title section-title-academy">
                    {t("home.roadmapTitle")}
                </h2>
                <p className="roadmap-text">
                    {t("home.roadmapText")}
                </p>
            </div>

            {/* Imagen centrada */}
            <div className="roadmap-image-wrapper">
                <img
                src={fotoRoadmap}
                alt="Roadmap TheQAHub 2026"
                className="roadmap-image"
                width="1920"
                height="1080"
                loading="lazy"
                decoding="async"
                />
            </div>

            {/* Texto inferior */}
            <div className="roadmap-bottom">
                <p className="roadmap-text">
                {t("home.roadmapBottom")}
                </p>

                <div className="roadmap-actions">
                <a href="https://academy.theqahub.es" className="details-btn" target="_blank" rel="noreferrer">
                    {t("home.goAcademy")}
                </a>

                <a
                    href={fotoRoadmap}
                    className="details-btn details-btn-outline"
                    download
                >
                    {t("home.downloadRoadmap")}
                </a>
                </div>
            </div>

            </section>


            {/* Barra separadora */}
            <hr className="section-divider" />

            {/* Sección de Servicios */}
            <section className="services-container">
                <div className="section-heading-stack section-heading-stack-center services-heading">
                    <span className="section-kicker">{t("home.kickerServices")}</span>
                    <h2 className="section-title-academy">{renderBrandText(t("home.servicesTitle"))}</h2>
                    <p className="services-description">
                        {t("home.servicesDescription")}
                    </p>
                </div>

                {/* Opciones de Servicios */}
                <div className="services-options">
                    {Object.keys(serviceDetails).map((key) => (
                        <button
                            key={key}
                            className={`service-option ${selectedService === key ? "active" : ""}`}
                            onClick={() => setSelectedService(key)}
                        >
                            <span className="service-icon-wrap">
                                {key === "web" && <FaLaptopCode className="service-icon" />}
                                {key === "mobile" && <FaMobileAlt className="service-icon" />}
                                {key === "firmware" && <FaMicrochip className="service-icon" />}
                                {key === "desktop" && <FaDesktop className="service-icon" />}
                            </span>
                            <span className="service-option-title">{serviceDetails[key].title}</span>
                        </button>
                    ))}
                </div>

                {/* Contenedor en dos columnas */}
                <div className="service-display">
                    {/* Caja de contenido con borde y descripción */}
                    {isMobile ? (
                        <>
                            <div 
                                className={`service-box ${isMobile ? 'has-background' : ''}`}
                                style={isMobile ? { '--service-image': `url(${serviceDetails[selectedService].optimizedImage})` } : {}}>
                                {/* Overlay oscuro para mejorar la legibilidad */}
                                <div className="service-content-overlay"></div>
                                <div className="service-content-box">
                                    {/* Ícono Representativo */}
                                    {selectedService === "web" && <FaLaptopCode className="service-icon" />}
                                    {selectedService === "mobile" && <FaMobileAlt className="service-icon" />}
                                    {selectedService === "firmware" && <FaMicrochip className="service-icon" />}
                                    {selectedService === "desktop" && <FaDesktop className="service-icon" />}

                                    {/* Título y Descripción */}
                                    <h3>{serviceDetails[selectedService].title.toUpperCase()}</h3>
                                    <p className="service-extended-description">{t(`home.services.${selectedService}`)}</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="service-box">
                                <div className="service-content-box">
                                    {/* Ícono Representativo */}
                                    {selectedService === "web" && <FaLaptopCode className="service-icon" />}
                                    {selectedService === "mobile" && <FaMobileAlt className="service-icon" />}
                                    {selectedService === "firmware" && <FaMicrochip className="service-icon" />}
                                    {selectedService === "desktop" && <FaDesktop className="service-icon" />}

                                    {/* Título y Descripción */}
                                    <h3>{serviceDetails[selectedService].title.toUpperCase()}</h3>
                                    <p className="service-extended-description">{t(`home.services.${selectedService}`)}</p>
                                </div>
                            </div>
                            {/* Imagen con overlay */}
                            <div className="service-image-container">
                                <img
                                    src={serviceDetails[selectedService].optimizedImage}
                                    alt={serviceDetails[selectedService].title}
                                    width="900"
                                    height="675"
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div className="service-image-overlay">
                                    <span className="service-number">0{Object.keys(serviceDetails).indexOf(selectedService) + 1}</span>
                                    <span className="service-name">{serviceDetails[selectedService].title.toUpperCase()}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* Barra separadora */}
            <hr className="section-divider" />

            <section className="academy-banner-section">
                <div className="academy-banner-shell">
                    <div className="academy-banner-copy section-heading-stack">
                        <span className="academy-banner-kicker">{t("home.academyKicker")}</span>
                        <h2 className="section-title-academy">{t("home.academyTitle")}</h2>
                        <p>
                            {renderBrandText(t("home.academyText"))}
                        </p>
                        <a
                            href="https://academy.theqahub.es"
                            target="_blank"
                        rel="noreferrer"
                        className="academy-banner-cta"
                    >
                            {t("home.goAcademy")}
                        </a>
                    </div>
                    <a
                        href="https://academy.theqahub.es"
                        target="_blank"
                        rel="noreferrer"
                        className="academy-banner-link"
                        aria-label={t("academy.kicker")}
                    >
                        <img
                            src={academyBanner}
                            alt={t("home.academyImageAlt")}
                            className="academy-banner-image"
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
            </section>

            {/* Barra separadora */}
            <hr className="section-divider" />

            <BlogDetailsHome />

            {/* Línea divisoria elegante */}
            <hr className="section-divider" />

            <section className="connect-cta-section">
                <div className="connect-cta-content">
                    <div className="section-heading-stack section-heading-stack-center connect-heading-stack">
                        <span className="section-kicker">{t("common.contactKicker")}</span>
                        <div className="letters connect-cta-title">
                            {isMobile ? (
                                <h2 className="mobile-connect-title">{t("common.connect")}</h2>
                            ) : (
                                <TextPressure
                                    text={t("common.connect")}
                                    fontFamily="Bogart"
                                    textColor="#5f7868"
                                    minFontSize={44}
                                    maxFontSize={138}
                                    movementIntensity={10}
                                    className="connect-pressure-title"
                                />
                            )}
                        </div>
                    </div>
                    <p className="connect-cta-lead">{t("common.connectLead")}</p>
                    <div className="connect-cta-tags" aria-label={t("common.connectTagsLabel")}>
                        <span>{t("common.connectTag1")}</span>
                        <span>{t("common.connectTag2")}</span>
                        <span>{t("common.connectTag3")}</span>
                    </div>
                    <div className="contact-button-border">
                        <div className="contact-button-display">
                            <a href="/contacto" className="contact-button-sections">
                                <GradientText
                                    colors={["#5f7868", "#97b79e", "#5f7868", "#97b79e", "#5f7868"]}
                                    animationSpeed={3}
                                    showBorder={false}
                                    className="custom-class"
                                >
                                    {t("common.contactMe")}
                                </GradientText>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Línea divisoria elegante */}
            <hr className="section-divider-about-me" />
            <ContactInfo/>
        </>
    );
}

export default HomeDetails;
